import { getApiErrorMessage } from '../services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Eye, EyeOff, CheckCircle, Shield, ChevronLeft, Building2, User, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import eyvoLogo from '../assets/eyvo_logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: 'starter',      name: 'Starter',      price: '$15/user/mo', users: '10 users',   desc: 'Requisitions + Purchase Orders',      color: 'border-muted' },
  { id: 'professional', name: 'Professional', price: '$25/user/mo', users: '50 users',   desc: 'Starter + RFQ, Contracts, AP Matching', color: 'border-primary', popular: true },
  { id: 'enterprise',   name: 'Enterprise',   price: 'Custom',      users: 'Unlimited',  desc: 'All 10 modules + dedicated support',    color: 'border-muted' },
];

const ALL_MODULES = [
  { id: 'requisitions',   name: 'Purchase Requisitions',  plans: ['starter','professional','enterprise'] },
  { id: 'orders',         name: 'Purchase Orders',         plans: ['starter','professional','enterprise'] },
  { id: 'rfq',            name: 'RFQ / RFI Sourcing',      plans: ['professional','enterprise'] },
  { id: 'contracts',      name: 'Contract Management',     plans: ['professional','enterprise'] },
  { id: 'goods_receiving',name: 'Receiving & AP',    plans: ['professional','enterprise'] },
  { id: 'assets',         name: 'Asset Management',        plans: ['enterprise'] },
  { id: 'inventory',      name: 'Inventory Control',       plans: ['enterprise'] },
  { id: 'expenses',       name: 'Employee Expenses',       plans: ['enterprise'] },
  { id: 'supplier_portal',name: 'Supplier Portal',         plans: ['enterprise'] },
  { id: 'sales_orders',   name: 'Sales Order Processing',  plans: ['enterprise'] },
];

// localStorage keys
const STORAGE_KEYS = {
  leadId: 'eyvo_onboarding_lead_id',
  stage: 'eyvo_onboarding_stage',
  plan: 'eyvo_onboarding_plan',
  formData: 'eyvo_onboarding_form'
};

export default function SelfOnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Core state
  const [stage, setStage] = useState(1); // 1=lead capture, 2=plan selection, 3=admin setup, 4=success
  const [leadId, setLeadId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  
  // Form state
  const [leadForm, setLeadForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    city: '',
    country: 'United States',
    access_code: ''
  });
  
  const [adminForm, setAdminForm] = useState({
    admin_email: '',
    admin_password: '',
    confirm_password: '',
    auth_method: 'email_password',
    seed_test_data: false  // New field
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [version, setVersion] = useState(null);

  // Helper to update form fields
  const updateLeadForm = (key, value) => setLeadForm(prev => ({ ...prev, [key]: value }));
  const updateAdminForm = (key, value) => setAdminForm(prev => ({ ...prev, [key]: value }));

  // Load version info
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/version`)
      .then(r => setVersion(r.data))
      .catch(() => {});
  }, []);

  // Restore progress from localStorage on mount
  useEffect(() => {
    try {
      const savedLeadId = localStorage.getItem(STORAGE_KEYS.leadId);
      const savedStage = localStorage.getItem(STORAGE_KEYS.stage);
      const savedPlan = localStorage.getItem(STORAGE_KEYS.plan);
      const savedFormData = localStorage.getItem(STORAGE_KEYS.formData);

      if (savedLeadId) setLeadId(savedLeadId);
      if (savedStage) setStage(parseInt(savedStage));
      if (savedPlan) setSelectedPlan(savedPlan);
      if (savedFormData) {
        const parsedForm = JSON.parse(savedFormData);
        setLeadForm(parsedForm.lead || leadForm);
        setAdminForm(parsedForm.admin || adminForm);
      }
    } catch (e) {
      console.error('Failed to restore onboarding state:', e);
    }
  }, []);

  // Save progress to localStorage whenever state changes
  useEffect(() => {
    try {
      if (leadId) localStorage.setItem(STORAGE_KEYS.leadId, leadId);
      localStorage.setItem(STORAGE_KEYS.stage, stage.toString());
      localStorage.setItem(STORAGE_KEYS.plan, selectedPlan);
      localStorage.setItem(STORAGE_KEYS.formData, JSON.stringify({ lead: leadForm, admin: adminForm }));
    } catch (e) {
      console.error('Failed to save onboarding state:', e);
    }
  }, [leadId, stage, selectedPlan, leadForm, adminForm]);

  // Clear localStorage when successfully completed
  const clearProgress = () => {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  };

  // Stage 1: Capture lead
  const submitStage1 = async () => {
    if (!leadForm.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!leadForm.contact_name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    if (!leadForm.email.includes('@')) {
      toast.error('Valid email is required');
      return;
    }
    if (leadForm.access_code !== (process.env.REACT_APP_ONBOARD_CODE || '')) {
      toast.error('Invalid access code. Please contact Eyvo for access.');
      return;
    }

    setLoading(true);
    try {
      const { access_code, ...payload } = leadForm;
      const res = await axios.post(`${BACKEND_URL}/api/leads/capture`, payload);
      setLeadId(res.data.lead_id);
      toast.success('Contact information saved');
      setStage(2);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to save contact information'));
    }
    setLoading(false);
  };

  // Stage 2: Update lead with plan
  const submitStage2 = async () => {
    if (!leadId) {
      toast.error('Session expired. Please start again.');
      return;
    }

    // Handle Enterprise plan differently
    if (selectedPlan === 'enterprise') {
      navigate('/enterprise-contact', { state: { leadId, leadForm } });
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/leads/update/${leadId}`, { plan: selectedPlan });
      toast.success(`${PLANS.find(p => p.id === selectedPlan)?.name} plan selected`);
      setStage(3);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to save plan selection'));
    }
    setLoading(false);
  };

  // Stage 3: Create tenant + admin
  const submitStage3 = async () => {
    if (!adminForm.admin_email.includes('@')) {
      toast.error('Valid admin email is required');
      return;
    }
    if (adminForm.admin_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (adminForm.admin_password !== adminForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const planModules = ALL_MODULES.filter(m => m.plans.includes(selectedPlan)).map(m => m.id);
      
      const res = await axios.post(`${BACKEND_URL}/api/tenants/self-onboard`, {
        company_name: leadForm.company_name,
        company_address: '',
        company_city: leadForm.city,
        company_country: leadForm.country,
        admin_email: adminForm.admin_email,
        admin_password: adminForm.admin_password,
        plan: selectedPlan,
        modules: planModules,
        system_currency: 'USD',
        auth_method: adminForm.auth_method,
        seed_test_data: adminForm.seed_test_data  // Pass flag to backend
      });

      // Update lead as completed
      await axios.put(`${BACKEND_URL}/api/leads/update/${leadId}`, {
        completed: true,
        tenant_id: res.data.tenant_id
      });

      setResult(res.data);
      setStage(4);
      clearProgress();
      toast.success('Account created successfully!');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Registration failed. Please try again.'));
    }
    setLoading(false);
  };

  const goBack = () => {
    if (stage > 1 && stage < 4) setStage(stage - 1);
    else navigate('/login');
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'hsl(var(--background))' }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-12" style={{ backgroundColor: 'hsl(var(--sidebar))' }}>
        <div>
          <img src={eyvoLogo} alt="Eyvo" className="h-12 w-auto mb-12" style={{ filter: 'brightness(0) invert(1)' }} />
          <h1 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Start your free 30-day trial
          </h1>
          <p className="text-white/70 text-sm leading-relaxed mb-8">
            Join hundreds of enterprises using Eyvo to streamline procurement, manage suppliers, and cut costs.
          </p>
          <div className="space-y-3">
            {['No credit card required', 'Full access for 30 days', 'Set up in under 5 minutes', 'WCAG 2.1 AA accessible', 'GDPR compliant'].map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span className="text-white/80 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} Eyvo eProcurement</p>
          {version && (
            <p className="text-white/20 text-[10px] font-mono">
              v{version.version} • {new Date(version.deployed_at).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles'
              })}
            </p>
          )}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-lg">
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-6">
            <button 
              onClick={goBack} 
              className="text-muted-foreground hover:text-foreground"
              data-testid="onboarding-back-button"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {[1, 2, 3].map(n => (
                <div 
                  key={n} 
                  className={`w-2 h-2 rounded-full transition-colors ${
                    stage >= n ? 'bg-primary' : 'bg-muted'
                  }`} 
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground ml-1">
              Step {Math.min(stage, 3)} of 3
            </span>
          </div>

          {/* Stage 1: Lead Capture */}
          {stage === 1 && (
            <div className="space-y-6" data-testid="onboarding-stage-1">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  Let's get started
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('term.tellUsOrganisation')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_name"
                      data-testid="lead-company-name-input"
                      value={leadForm.company_name}
                      onChange={e => updateLeadForm('company_name', e.target.value)}
                      placeholder="e.g. Acme Corporation"
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="contact_name">Your Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="contact_name"
                      data-testid="lead-contact-name-input"
                      value={leadForm.contact_name}
                      onChange={e => updateLeadForm('contact_name', e.target.value)}
                      placeholder="e.g. John Smith"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="lead-email-input"
                      value={leadForm.email}
                      onChange={e => updateLeadForm('email', e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="lead-phone-input"
                      value={leadForm.phone}
                      onChange={e => updateLeadForm('phone', e.target.value)}
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      data-testid="lead-city-input"
                      value={leadForm.city}
                      onChange={e => updateLeadForm('city', e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    data-testid="lead-country-input"
                    value={leadForm.country}
                    onChange={e => updateLeadForm('country', e.target.value)}
                    placeholder="United States"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="access_code">Access Code *</Label>
                  <Input
                    id="access_code"
                    type="password"
                    data-testid="lead-access-code-input"
                    value={leadForm.access_code}
                    onChange={e => updateLeadForm('access_code', e.target.value)}
                    placeholder="Enter your access code"
                  />
                  <p className="text-xs text-muted-foreground">Contact Eyvo to obtain your access code</p>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={submitStage1} 
                disabled={loading}
                data-testid="stage-1-continue-button"
              >
                {loading ? 'Saving...' : 'Continue'}
              </Button>
            </div>
          )}

          {/* Stage 2: Plan Selection */}
          {stage === 2 && (
            <div className="space-y-6" data-testid="onboarding-stage-2">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  Choose your plan
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  You can change or upgrade at any time
                </p>
              </div>

              <div className="space-y-3">
                {PLANS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlan(p.id)}
                    data-testid={`plan-tile-${p.id}`}
                    className={`w-full text-left border-2 rounded-xl p-4 transition-all ${
                      selectedPlan === p.id ? 'border-primary bg-primary/5' : p.color + ' hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{p.name}</span>
                          {p.popular && <Badge className="text-[10px] bg-primary">Most Popular</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                        <p className="text-xs text-muted-foreground mt-1">{p.users}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{p.price}</p>
                        {p.id !== 'enterprise' && <p className="text-[10px] text-muted-foreground">after trial</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="border rounded-xl p-4 bg-muted/20">
                <p className="text-xs font-medium mb-2">Included modules:</p>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_MODULES.map(m => (
                    <span
                      key={m.id}
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${
                        m.plans.includes(selectedPlan)
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-muted text-muted-foreground border-transparent'
                      }`}
                    >
                      {m.name}
                    </span>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={submitStage2} 
                disabled={loading}
                data-testid="stage-2-continue-button"
              >
                {loading ? 'Saving...' : `Continue with ${PLANS.find(p => p.id === selectedPlan)?.name}`}
              </Button>
            </div>
          )}

          {/* Stage 3: Admin Setup */}
          {stage === 3 && (
            <div className="space-y-6" data-testid="onboarding-stage-3">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  Create admin account
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Set up the first administrator for {leadForm.company_name}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="admin_email">Admin Email *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    data-testid="admin-email-input"
                    value={adminForm.admin_email}
                    onChange={e => updateAdminForm('admin_email', e.target.value)}
                    placeholder="admin@company.com"
                    autoFocus
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin_password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="admin_password"
                      type={showPw ? 'text' : 'password'}
                      data-testid="admin-password-input"
                      value={adminForm.admin_password}
                      onChange={e => updateAdminForm('admin_password', e.target.value)}
                      placeholder="Min 8 characters"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    data-testid="admin-confirm-password-input"
                    value={adminForm.confirm_password}
                    onChange={e => updateAdminForm('confirm_password', e.target.value)}
                    placeholder="Repeat password"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="auth_method">Authentication Method</Label>
                  <select
                    id="auth_method"
                    value={adminForm.auth_method}
                    onChange={e => updateAdminForm('auth_method', e.target.value)}
                    className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    data-testid="auth-method-select"
                  >
                    <option value="email_password">Email & Password (Default)</option>
                    <option value="google_oauth">Google Workspace / OAuth</option>
                    <option value="microsoft_entra">Microsoft Entra ID</option>
                    <option value="okta_saml">Okta SAML</option>
                    <option value="onelogin">OneLogin</option>
                    <option value="auth0">Auth0</option>
                    <option value="ping_identity">Ping Identity</option>
                    <option value="ldap">LDAP / Active Directory</option>
                    <option value="jumpcloud">JumpCloud</option>
                    <option value="custom_saml">Custom SAML</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    You can configure SSO details later in admin settings
                  </p>
                </div>

                <div className="border rounded-xl p-4 bg-primary/5 space-y-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="seed_test_data"
                      checked={adminForm.seed_test_data}
                      onChange={e => updateAdminForm('seed_test_data', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-input cursor-pointer"
                      data-testid="seed-test-data-checkbox"
                    />
                    <div>
                      <label htmlFor="seed_test_data" className="text-sm font-medium cursor-pointer">
                        Seed database with test data
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Populate your database with 100+ sample records (suppliers, items, requisitions, contracts, etc.) 
                        so you can explore and test the system immediately. You can delete this data anytime.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={submitStage3} 
                disabled={loading}
                data-testid="stage-3-submit-button"
              >
                {loading ? t('term.creatingOrganisation') : 'Start Free Trial'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By signing up you agree to Eyvo's Terms of Service and Privacy Policy.
              </p>
            </div>
          )}

          {/* Stage 4: Success */}
          {stage === 4 && result && (
            <div className="text-center space-y-5" data-testid="onboarding-success">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  You're all set!
                </h2>
                <p className="text-muted-foreground mt-1">{result.message}</p>
              </div>
              <div className="border rounded-xl p-4 bg-muted/20 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Company Code</span>
                  <span className="font-mono font-bold text-primary" data-testid="success-tenant-code">
                    {result.tenant_id}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Admin Email</span>
                  <span>{result.admin_email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="capitalize">{result.plan}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Trial Period</span>
                  <span>{result.trial_days} days</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <strong>Save your Company Code:</strong>{' '}
                <code className="font-mono bg-amber-100 px-1 rounded">{result.tenant_id}</code> — you'll
                need this every time you log in.
              </div>
              <Button 
                className="w-full" 
                onClick={() => navigate(`/login?tenant=${result.tenant_id}`)}
                data-testid="success-goto-login-button"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
